import { useState, useEffect } from "react";
import { Bell, AlertTriangle, CheckCircle, X, AlertOctagon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AlertsPanel = ({ onAlertCountChange }) => {
  const [alerts, setAlerts] = useState([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = async () => {
    try {
      const [alertsRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/alerts?limit=50`),
        axios.get(`${API_URL}/api/alerts/unacknowledged/count`)
      ]);
      setAlerts(alertsRes.data);
      setUnacknowledgedCount(countRes.data.count);
      if (onAlertCountChange) {
        onAlertCountChange(countRes.data.count);
      }
    } catch (err) {
      console.error("Error fetching alerts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/api/alerts/${alertId}/acknowledge`);
      fetchAlerts();
    } catch (err) {
      console.error("Error acknowledging alert:", err);
    }
  };

  const getAlertIcon = (eventType) => {
    switch (eventType) {
      case 'overflow':
        return <AlertOctagon className="w-4 h-4 text-red-500" />;
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-amber-400" />;
      case 'low':
        return <AlertTriangle className="w-4 h-4 text-blue-400" />;
      default:
        return <Bell className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getAlertColor = (eventType) => {
    switch (eventType) {
      case 'overflow':
        return 'bg-red-500/20 border-red-500/50 text-red-400';
      case 'critical':
        return 'bg-red-500/10 border-red-500/30 text-red-300';
      case 'warning':
        return 'bg-amber-500/10 border-amber-500/30 text-amber-300';
      case 'low':
        return 'bg-blue-500/10 border-blue-500/30 text-blue-300';
      default:
        return 'bg-zinc-500/10 border-zinc-500/30 text-zinc-300';
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative text-zinc-400 hover:text-white h-10 w-10 p-0"
        onClick={() => setShowDialog(true)}
        data-testid="alerts-button"
      >
        <Bell className="w-[38px] h-[38px]" />
        {unacknowledgedCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {unacknowledgedCount > 9 ? '9+' : unacknowledgedCount}
          </span>
        )}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Bell className="w-14 h-14" />
              Historial de Alertas
              {unacknowledgedCount > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {unacknowledgedCount} sin reconocer
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh] space-y-2 pr-2">
            {loading ? (
              <div className="text-center text-zinc-500 py-8">Cargando...</div>
            ) : alerts.length === 0 ? (
              <div className="text-center text-zinc-500 py-8">
                <CheckCircle className="w-14 h-14 mx-auto mb-2 text-green-500/50" />
                No hay alertas registradas
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg border ${getAlertColor(alert.event_type)} ${
                    !alert.acknowledged ? 'ring-1 ring-red-500/50' : 'opacity-60'
                  }`}
                  data-testid={`alert-${alert.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      {getAlertIcon(alert.event_type)}
                      <div>
                        <div className="font-semibold text-sm">
                          {alert.sensor_name} - {alert.event_type.toUpperCase()}
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">
                          {alert.message}
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-1">
                          {new Date(alert.created_at).toLocaleString('es-ES')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {alert.acknowledged ? (
                        <Badge variant="outline" className="text-green-400 border-green-400/30 text-[10px]">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Reconocida
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-zinc-600 hover:bg-zinc-800"
                          onClick={() => acknowledgeAlert(alert.id)}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Reconocer
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AlertsPanel;
